import React, { useState } from 'react';
import { PlusIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, UsersIcon, UserPlusIcon, AlertTriangleIcon, ClockIcon, CheckCircleIcon, CalendarIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, BellIcon, CheckIcon, XIcon } from 'lucide-react';
import { RequisitionList } from './procurement/RequisitionList';
import { PurchaseOrderList } from './procurement/PurchaseOrderList';
import { formatCurrency } from './utils/currencyFormatter';
import { Remi } from './ai/Remi';
export function Dashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for new dashboard sections
  const vendorStats = {
    pendingOnboarding: 5,
    failingCompliance: 3,
    awaitingReview: 7
  };
  const invoiceStats = {
    inProcessing: 12,
    failingCompliance: 4,
    approvedForPayment: 8
  };
  const vendorsNeedingAction = [{
    id: 'VEN-2023-004',
    name: 'Global Supply Co.',
    status: 'Incomplete',
    issue: 'Missing tax information',
    dueDate: '2023-11-15'
  }, {
    id: 'VEN-2023-005',
    name: 'Tech Solutions Inc.',
    status: 'Flagged',
    issue: 'Insurance certificate expired',
    dueDate: '2023-11-10'
  }, {
    id: 'VEN-2023-006',
    name: 'Office Essentials',
    status: 'Awaiting Review',
    issue: 'New onboarding submission',
    dueDate: '2023-11-20'
  }];
  const invoicesNeedingAttention = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-001234',
    vendor: 'Dell Technologies',
    issue: 'Unmatched PO',
    status: 'Error',
    amount: 4500.0,
    currency: 'USD',
    dueDate: '2023-11-12'
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-005678',
    vendor: 'Office Depot',
    issue: 'Missing GRN',
    status: 'Warning',
    amount: 1250.75,
    currency: 'USD',
    dueDate: '2023-11-15'
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-009012',
    vendor: 'PrintWorks Inc.',
    issue: 'Currency mismatch',
    status: 'Error',
    amount: 3200.5,
    currency: 'EUR',
    dueDate: '2023-11-18'
  }];
  const upcomingRenewals = [{
    id: 'DOC-2023-001',
    vendor: 'Dell Technologies',
    documentType: 'Insurance Certificate',
    expiryDate: '2023-11-30',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-002',
    vendor: 'Global Supply Co.',
    documentType: 'Tax Compliance',
    expiryDate: '2023-12-05',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-003',
    vendor: 'Tech Solutions Inc.',
    documentType: 'Business License',
    expiryDate: '2023-12-15',
    status: 'Expiring Soon'
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-3686">
      <div className="mb-8 flex justify-between items-center" data-id="element-3687">
        <div data-id="element-3688">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3689">
            P2P Control Centre
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3690">
            Comprehensive overview of procurement, vendor management, and
            accounts payable
          </p>
        </div>
        <div className="flex items-center" data-id="element-3691">
          <div className="flex items-center" data-id="element-3692">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3693" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3694">
              <option value="7days" data-id="element-3695">Last 7 days</option>
              <option value="30days" data-id="element-3696">Last 30 days</option>
              <option value="90days" data-id="element-3697">Last 90 days</option>
              <option value="ytd" data-id="element-3698">Year to date</option>
            </select>
          </div>
        </div>
      </div>
      {/* Remi AI Assistant */}
      <div className="mb-8" data-id="element-3699">
        <Remi setCurrentView={setCurrentView} data-id="element-3700" />
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-3701">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3702">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3703">
          {/* Existing cards */}
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3704">
            <div className="p-5" data-id="element-3705">
              <div className="flex items-center" data-id="element-3706">
                <div className="flex-shrink-0" data-id="element-3707">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-3708" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3709">
                  <dl data-id="element-3710">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3711">
                      Total Requisitions
                    </dt>
                    <dd data-id="element-3712">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3713">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3714">
            <div className="p-5" data-id="element-3715">
              <div className="flex items-center" data-id="element-3716">
                <div className="flex-shrink-0" data-id="element-3717">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-3718" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3719">
                  <dl data-id="element-3720">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3721">
                      Pending Approval
                    </dt>
                    <dd data-id="element-3722">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3723">
                        {pendingApproval}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-3724">
            <div className="p-5" data-id="element-3725">
              <div className="flex items-center" data-id="element-3726">
                <div className="flex-shrink-0" data-id="element-3727">
                  <ClipboardCheckIcon className="h-6 w-6 text-indigo-400" data-id="element-3728" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3729">
                  <dl data-id="element-3730">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3731">
                      Purchase Orders
                    </dt>
                    <dd data-id="element-3732">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3733">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-3734">
            <div className="p-5" data-id="element-3735">
              <div className="flex items-center" data-id="element-3736">
                <div className="flex-shrink-0" data-id="element-3737">
                  <TrendingUpIcon className="h-6 w-6 text-green-400" data-id="element-3738" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3739">
                  <dl data-id="element-3740">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3741">
                      Total Spend
                    </dt>
                    <dd data-id="element-3742">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3743">
                        {formatCurrency(totalReqSpend + totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Vendor Status Summary Cards */}
      <div className="mb-8" data-id="element-3744">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3745">
          Vendor Management
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3746">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3747">
            <div className="p-5" data-id="element-3748">
              <div className="flex items-center" data-id="element-3749">
                <div className="flex-shrink-0" data-id="element-3750">
                  <UserPlusIcon className="h-6 w-6 text-yellow-400" data-id="element-3751" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3752">
                  <dl data-id="element-3753">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3754">
                      Vendors Pending Onboarding
                    </dt>
                    <dd data-id="element-3755">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3756">
                        {vendorStats.pendingOnboarding}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('compliance-check')} data-id="element-3757">
            <div className="p-5" data-id="element-3758">
              <div className="flex items-center" data-id="element-3759">
                <div className="flex-shrink-0" data-id="element-3760">
                  <AlertTriangleIcon className="h-6 w-6 text-orange-400" data-id="element-3761" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3762">
                  <dl data-id="element-3763">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3764">
                      <span className="group relative" data-id="element-3765">
                        Vendors Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3766">
                          Includes expired insurance, missing tax details, etc.
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3767">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3768">
                        {vendorStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3769">
            <div className="p-5" data-id="element-3770">
              <div className="flex items-center" data-id="element-3771">
                <div className="flex-shrink-0" data-id="element-3772">
                  <ClockIcon className="h-6 w-6 text-blue-400" data-id="element-3773" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3774">
                  <dl data-id="element-3775">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3776">
                      Vendors Awaiting Review
                    </dt>
                    <dd data-id="element-3777">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3778">
                        {vendorStats.awaitingReview}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Summary Cards */}
      <div className="mb-8" data-id="element-3779">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3780">
          Accounts Payable
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3781">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3782">
            <div className="p-5" data-id="element-3783">
              <div className="flex items-center" data-id="element-3784">
                <div className="flex-shrink-0" data-id="element-3785">
                  <ReceiptIcon className="h-6 w-6 text-purple-400" data-id="element-3786" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3787">
                  <dl data-id="element-3788">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3789">
                      Invoices in Processing
                    </dt>
                    <dd data-id="element-3790">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3791">
                        {invoiceStats.inProcessing}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('matching')} data-id="element-3792">
            <div className="p-5" data-id="element-3793">
              <div className="flex items-center" data-id="element-3794">
                <div className="flex-shrink-0" data-id="element-3795">
                  <AlertCircleIcon className="h-6 w-6 text-red-400" data-id="element-3796" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3797">
                  <dl data-id="element-3798">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3799">
                      <span className="group relative" data-id="element-3800">
                        Invoices Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3801">
                          Unmatched PO, blocked vendor, tax or value mismatches
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3802">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3803">
                        {invoiceStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3804">
            <div className="p-5" data-id="element-3805">
              <div className="flex items-center" data-id="element-3806">
                <div className="flex-shrink-0" data-id="element-3807">
                  <CreditCardIcon className="h-6 w-6 text-green-400" data-id="element-3808" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3809">
                  <dl data-id="element-3810">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3811">
                      Invoices Approved for Payment
                    </dt>
                    <dd data-id="element-3812">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3813">
                        {invoiceStats.approvedForPayment}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3814">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3815">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3816">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3817">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3818">
              View all
            </button>
          </div>
          <RequisitionList requisitions={recentRequisitions} locale={locale} data-id="element-3819" />
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3820">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3821">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3822">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3823">
              View all
            </button>
          </div>
          <PurchaseOrderList purchaseOrders={recentPurchaseOrders} locale={locale} data-id="element-3824" />
        </div>
      </div>
      {/* New Dashboard Sections */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3825">
        {/* Vendors Needing Action */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3826">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3827">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3828">
              Vendors Needing Action
            </h3>
            <button onClick={() => setCurrentView('vendor-management')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3829">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3830">
            {vendorsNeedingAction.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3831">
                No vendors need attention at this time.
              </li> : vendorsNeedingAction.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('vendor-management')} data-id="element-3832">
                  <div className="flex items-center justify-between" data-id="element-3833">
                    <div className="flex items-center" data-id="element-3834">
                      <div className="flex-shrink-0" data-id="element-3835">
                        {vendor.status === 'Incomplete' ? <XIcon className="h-5 w-5 text-red-400" data-id="element-3836" /> : vendor.status === 'Flagged' ? <AlertTriangleIcon className="h-5 w-5 text-orange-400" data-id="element-3837" /> : <ClockIcon className="h-5 w-5 text-blue-400" data-id="element-3838" />}
                      </div>
                      <div className="ml-3" data-id="element-3839">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3840">
                          {vendor.name}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-3841">{vendor.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-3842">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-3843">
                        Due: {vendor.dueDate}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('vendor-management');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3844">
                        Review
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Invoices Needing Attention */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3845">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3846">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3847">
              Invoices Needing Attention
            </h3>
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3848">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3849">
            {invoicesNeedingAttention.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3850">
                No invoices need attention at this time.
              </li> : invoicesNeedingAttention.map(invoice => <li key={invoice.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('invoice-list')} data-id="element-3851">
                  <div className="flex items-center justify-between" data-id="element-3852">
                    <div className="flex items-center" data-id="element-3853">
                      <div className="flex-shrink-0" data-id="element-3854">
                        {invoice.status === 'Error' ? <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-3855" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-3856" />}
                      </div>
                      <div className="ml-3" data-id="element-3857">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3858">
                          {invoice.invoiceNumber} - {invoice.vendor}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-3859">{invoice.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-3860">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-3861">
                        {formatCurrency(invoice.amount, invoice.currency, locale)}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('matching');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3862">
                        Resolve
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
      {/* Upcoming Renewals / Expiries */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-3863">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3864">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3865">
            Upcoming Renewals & Expiries
          </h3>
          <button onClick={() => setCurrentView('compliance-check')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3866">
            View all
          </button>
        </div>
        <ul className="divide-y divide-gray-200" data-id="element-3867">
          {upcomingRenewals.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3868">
              No upcoming renewals or expiries.
            </li> : upcomingRenewals.map(renewal => <li key={renewal.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('compliance-check')} data-id="element-3869">
                <div className="flex items-center justify-between" data-id="element-3870">
                  <div className="flex items-center" data-id="element-3871">
                    <div className="flex-shrink-0" data-id="element-3872">
                      <CalendarIcon className="h-5 w-5 text-orange-400" data-id="element-3873" />
                    </div>
                    <div className="ml-3" data-id="element-3874">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3875">
                        {renewal.vendor} - {renewal.documentType}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3876">
                        Expires on {renewal.expiryDate}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-3877">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-3878">
                      <ClockIcon className="mr-1 h-3 w-3" data-id="element-3879" />
                      {renewal.status}
                    </span>
                  </div>
                </div>
              </li>)}
        </ul>
      </div>
      {/* Optional: Vendor Compliance Trend Chart */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8 p-5" data-id="element-3880">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3881">
          Vendor Compliance Trend (Last 3 Months)
        </h3>
        <div className="h-64 flex items-center justify-center" data-id="element-3882">
          <div className="w-full h-full flex items-end justify-around px-10" data-id="element-3883">
            <div className="flex flex-col items-center" data-id="element-3884">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '70%'
            }} data-id="element-3885"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3886">Sep</p>
              <p className="text-xs text-gray-400" data-id="element-3887">85%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-3888">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '82%'
            }} data-id="element-3889"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3890">Oct</p>
              <p className="text-xs text-gray-400" data-id="element-3891">92%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-3892">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '78%'
            }} data-id="element-3893"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3894">Nov</p>
              <p className="text-xs text-gray-400" data-id="element-3895">89%</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}